import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AuthorizationService } from 'app/core/services/authorization.service';
import {
    FEATURE_FLAGS,
    FeatureFlagService,
} from 'app/core/services/feature-flag.service';
import { SUBSCRIPTION_MANAGE_ACCESS } from 'app/shared/models/subscription.model';

/**
 * Plan-usage banner rendered in the shell above every page.
 *
 * SOFT (>= 80%): amber heads-up with the numbers.
 * HARD (>= 100%): red, names the exhausted resource and offers the upgrade
 * path BEFORE the user runs into the quota middleware — the create action
 * itself is disabled by the page (see ListAlbumComponent), so the limit is
 * never discovered by producing a 429.
 *
 * The upgrade link goes to the canonical `/pricing` catalog and only for a
 * principal who can actually buy (same gate as the pricing/billing pages);
 * everyone else is told to ask an organization administrator.
 */
@Component({
    selector: 'app-usage-banner',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [RouterLink, MatIconModule, TranslocoModule],
    templateUrl: './usage-banner.component.html',
})
export class UsageBannerComponent {
    private readonly _authorization = inject(AuthorizationService);
    private readonly _flags = inject(FeatureFlagService);

    /** 'NONE' | 'SOFT' | 'HARD'. */
    warningLevel = input<string>('NONE');

    albumsUsed = input<number>(0);
    albumsLimit = input<number>(0);
    photosUsed = input<number>(0);
    photosLimit = input<number>(0);

    /** HARD names the resource that ran out; albums take precedence over photos. */
    readonly messageKey = computed(() => {
        if (this.warningLevel() !== 'HARD') return 'quota.usage.soft';
        const albumsOut =
            this.albumsLimit() > 0 && this.albumsUsed() >= this.albumsLimit();
        return albumsOut ? 'quota.usage.hardAlbums' : 'quota.usage.hardPhotos';
    });

    readonly messageParams = computed(() => ({
        albumsUsed: this.albumsUsed(),
        albumsLimit: this.albumsLimit(),
        photosUsed: this.photosUsed(),
        photosLimit: this.photosLimit(),
    }));

    get billingEnabled(): boolean {
        return this._flags.isEnabled(FEATURE_FLAGS.BILLING);
    }

    get canUpgrade(): boolean {
        return this._authorization.canAccessAction(SUBSCRIPTION_MANAGE_ACCESS);
    }
}
